import React, { useEffect, useRef, useState } from "react";
import { useClientesApi } from "./clientesApi";
import { errorDeApi, infoDe } from "./clientesUtils";
import "../reservas/reservas-organizer.css";
import "./clientes.css";

/* "Vincular a ficha": el personal busca una ficha existente del salón y confirma. Nunca se vincula por coincidencia
   de datos: la decisión es siempre de una persona. Un turno de cuenta de Zoco no se vincula a mano (409). */

const DEMORA = 300;

const mensajeDeVinculo = (e, reserva) => {
  const { message, codigo, status } = errorDeApi(e, "No pudimos vincular "+(reserva ? "la reserva" : "el turno")+". Probá de nuevo.");
  if (codigo === "account_appointment") return reserva ? "Esta reserva es de una cuenta de Zoco y se asocia sola a la ficha de esa cuenta. Usá \"Crear ficha\" para abrirla." : "Este turno es de una cuenta de Zoco y se asocia solo a la ficha de esa cuenta. Usá \"Crear ficha\" para abrirla.";
  if (status === 403) return reserva ? "No tenés permiso para vincular reservas a una ficha." : "No tenés permiso para vincular turnos a una ficha.";
  if (status === 404) return `Esa ficha o ${reserva ? "esta reserva" : "este turno"} ya no están disponibles. Buscá de nuevo.`;
  return message;
};

const VincularFichaModal = ({ reservationId, onVinculado, onCuenta, onCerrar, tipo = "turno" }) => {
  const api = useClientesApi();
  const [texto, setTexto] = useState("");
  const [q, setQ] = useState("");
  const [retry, setRetry] = useState(0);
  const [lista, setLista] = useState({ loading: true, error: "", items: null });
  const [elegido, setElegido] = useState(null);
  const [vinculando, setVinculando] = useState(false);
  const [error, setError] = useState("");
  const pedido = useRef(0);
  const dialogRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQ(texto.trim()), DEMORA);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    const id = ++pedido.current;
    setLista((prev) => ({ loading: true, error: "", items: prev.items }));
    (async () => {
      try {
        const info = infoDe(await api.list({ q }));
        if (!info || !Array.isArray(info.items)) throw new Error("Invalid clients response");
        if (id === pedido.current) setLista({ loading: false, error: "", items: info.items });
      } catch (e) {
        if (id === pedido.current) setLista({ loading: false, error: errorDeApi(e, "No pudimos buscar clientes. Probá de nuevo.").message, items: null });
      }
    })();
  }, [api, q, retry]);

  const vincular = async () => {
    setVinculando(true);
    setError("");
    try {
      await api.linkReservation(reservationId, elegido._id);
      onVinculado();
    } catch (e) {
      const mensaje = mensajeDeVinculo(e, tipo === "reserva");
      // The appointment belongs to a Zoco account: the caller reloads it so the right action shows up.
      if (errorDeApi(e).codigo === "account_appointment") onCuenta(mensaje);
      else setError(mensaje);
    } finally {
      setVinculando(false);
    }
  };

  return (
    <div className="zr-overlay" onMouseDown={onCerrar}>
      <div
        className="zr-modal fc-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Vincular a ficha"
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onCerrar(); } }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="zr-modal__title">Vincular a ficha</h3>
        {elegido ? (
          <>
            <p className="zr-modal__text">
              Vas a vincular {tipo === "reserva" ? "esta reserva" : "este turno"} con la ficha de <strong>{elegido.name || "cliente sin nombre"}</strong>
              {elegido.phone ? ` (${elegido.phone})` : ""}. Su historial va a incluir {tipo === "reserva" ? "esta reserva" : "este turno"}.
            </p>
            {error && <div className="fc-error" role="alert">{error}</div>}
            <div className="zr-modal__actions">
              <button type="button" className="zr-btn zr-btn--ghost" disabled={vinculando} onClick={() => { setElegido(null); setError(""); }}>Elegir otra</button>
              <button type="button" className="zr-btn zr-btn--dark" disabled={vinculando} onClick={vincular}>
                {vinculando ? "Vinculando…" : "Vincular"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="zr-modal__text">Buscá la ficha por nombre, teléfono, email o DNI.</p>
            <input
              type="search"
              className="zr-input"
              aria-label="Buscar ficha"
              placeholder="Nombre, teléfono, email o DNI"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
            {error && <div className="fc-error" role="alert">{error}</div>}
            {lista.loading && !lista.items && <p role="status">Buscando…</p>}
            {lista.error && (
              <div className="fc-error" role="alert">
                {lista.error}
                <button type="button" className="zr-btn zr-btn--soft" onClick={() => setRetry((n) => n + 1)}>Reintentar</button>
              </div>
            )}
            {lista.items && lista.items.length === 0 && (
              <div className="fc-empty">
                <p>No encontramos fichas con esa búsqueda.</p>
                <p>Si es un cliente nuevo, cerrá esta ventana y usá "Crear ficha".</p>
              </div>
            )}
            {lista.items && lista.items.length > 0 && (
              <ul className="fc-results" aria-label="Fichas encontradas">
                {lista.items.map((c) => (
                  <li key={c._id}>
                    <button type="button" className="fc-row" onClick={() => { setError(""); setElegido(c); }}>
                      <span className="fc-row__name">{c.name || "Sin nombre"}</span>
                      <span className="fc-row__sub">{c.phone || "Sin teléfono"}</span>
                      <span className="fc-row__sub">{c.email || "Sin email"}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="zr-modal__actions">
              <button type="button" className="zr-btn zr-btn--ghost" onClick={onCerrar}>Cancelar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VincularFichaModal;
